/**
 * @fileoverview Shared helpers for chart value labels: per-bar label
 * collision guarding, density-adaptive bar sizing, and line-chart extrema
 * selection.
 * @module shared/utils/chartBars
 */

/**
 * Picks which bars get a value label. Every positive bar is labeled except
 * when two immediate neighbors sit at nearly the same height — their labels
 * would overlap horizontally, so only the taller of the two keeps its label
 * (ties keep the left one). Zero-value bars are never labeled and never
 * block their neighbors.
 *
 * @param values - Bar values in render order
 * @returns Set of indices that should render a label
 *
 * @example
 * computeLabeledBarIndices([2400, 2400, 0, 812]) // Set {0, 3}
 */
export function computeLabeledBarIndices(values: number[]): Set<number> {
  const indices = new Set<number>()
  const max = Math.max(...values, 0)
  if (max <= 0) return indices
  // Label y is proportional to value/max; below this delta two adjacent
  // labels sit close enough vertically to overlap horizontally.
  const minGap = max * 0.06
  let lastLabeled = -1
  values.forEach((value, i) => {
    if (value <= 0) return
    const prev = lastLabeled === i - 1 ? values[lastLabeled] : undefined
    if (prev !== undefined && Math.abs(value - prev) < minGap) {
      if (value > prev) {
        indices.delete(lastLabeled)
        indices.add(i)
        lastLabeled = i
      }
      // else: keep the (taller or equal) left label, skip this one. The
      // next bar is then 2 slots from the last label — safe by distance.
      return
    }
    indices.add(i)
    lastLabeled = i
  })
  return indices
}

/**
 * Density-adaptive bar width cap: sparse ranges (a week or so) get wider
 * bars so the chart doesn't look skeletal; dense ranges keep slim bars.
 * Clamped so 2-3 bar charts don't turn into chunky slabs.
 *
 * @param barCount - Number of bars in the chart
 * @returns Pixel value for recharts' maxBarSize
 */
export function adaptiveMaxBarSize(barCount: number): number {
  return Math.max(24, Math.min(56, Math.floor(720 / Math.max(barCount, 1))))
}

export interface LabeledExtremum {
  index: number
  kind: 'max' | 'min'
}

/**
 * Picks which points of a line chart get a value label: the turning points
 * of the curve. Tiny wiggles are filtered by a prominence threshold — an
 * interior extremum is dropped when its swing from the previously kept
 * extremum is below `minProminence` of the value range. The global max and
 * min are always labeled (even when they sit at the chart's endpoints,
 * which are never turning points).
 *
 * @param values - Series values in render order
 * @param minProminence - Minimum swing between kept extrema, as a fraction
 *   of the value range
 * @returns Labeled extrema in index order
 */
export function findLabeledExtrema(values: number[], minProminence = 0.08): LabeledExtremum[] {
  const n = values.length
  if (n < 3) return []
  const max = Math.max(...values)
  const min = Math.min(...values)
  const range = max - min
  if (range <= 0) return []
  const threshold = range * minProminence

  // Interior turning points: where the curve's direction flips. Plateaus
  // (equal neighbors) carry the previous direction.
  const candidates = new Map<number, 'max' | 'min'>()
  let dir: 0 | 1 | -1 = 0
  for (let i = 1; i < n; i++) {
    const d = Math.sign(values[i]! - values[i - 1]!) as 0 | 1 | -1
    if (d === 0) continue
    if (dir === 1 && d === -1) candidates.set(i - 1, 'max')
    else if (dir === -1 && d === 1) candidates.set(i - 1, 'min')
    dir = d
  }

  const globalIndices = new Set([values.indexOf(max), values.indexOf(min)])
  candidates.set(values.indexOf(max), 'max')
  candidates.set(values.indexOf(min), 'min')

  const kept: LabeledExtremum[] = []
  for (const [index, kind] of [...candidates.entries()].sort((a, b) => a[0] - b[0])) {
    const prev = kept.at(-1)
    const isMinorWiggle =
      prev !== undefined && Math.abs(values[index]! - values[prev.index]!) < threshold
    if (isMinorWiggle && !globalIndices.has(index)) continue
    kept.push({ index, kind })
  }
  return kept
}
